import { Button } from "@akuna/ui";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import { DocumentTypeSelector } from "@/components/pets/DocumentTypeSelector";

interface DocumentEditDialogLabels {
  title: string;
  documentName: string;
  documentNamePlaceholder: string;
  documentDate: string;
  documentTime: string;
  observations: string;
  observationsPlaceholder: string;
  cancel: string;
  save: string;
  saving: string;
}

interface DocumentEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  labels: DocumentEditDialogLabels;
  name: string;
  onNameChange: (value: string) => void;
  type: string;
  onTypeChange: (value: string) => void;
  date: string;
  onDateChange: (value: string) => void;
  time: string;
  onTimeChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  onSave: () => void;
  isSaving?: boolean;
}

export function DocumentEditDialog({
  open,
  onOpenChange,
  labels,
  name,
  onNameChange,
  type,
  onTypeChange,
  date,
  onDateChange,
  time,
  onTimeChange,
  description,
  onDescriptionChange,
  onSave,
  isSaving = false,
}: DocumentEditDialogProps) {
  const canSave = Boolean(name.trim() && type.trim() && date && time);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{labels.documentName}</p>
            <Input
              value={name}
              onChange={(event) => onNameChange(event.target.value)}
              placeholder={labels.documentNamePlaceholder}
              disabled={isSaving}
            />
          </div>
          <DocumentTypeSelector
            value={type}
            onChange={onTypeChange}
            disabled={isSaving}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">{labels.documentDate}</p>
              <Input
                type="date"
                value={date}
                onChange={(event) => onDateChange(event.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">{labels.documentTime}</p>
              <Input
                type="time"
                value={time}
                onChange={(event) => onTimeChange(event.target.value)}
                disabled={isSaving}
              />
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{labels.observations}</p>
            <Textarea
              value={description}
              onChange={(event) => onDescriptionChange(event.target.value)}
              placeholder={labels.observationsPlaceholder}
              rows={4}
              disabled={isSaving}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            {labels.cancel}
          </Button>
          <Button type="button" onClick={onSave} disabled={isSaving || !canSave}>
            {isSaving ? labels.saving : labels.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
